import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Eye, Clock, Camera, ExternalLink } from 'lucide-react';
import { Button, Card } from './ui';

const AdminDepositsPanel = ({ currentUser }) => {
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingDeposit, setProcessingDeposit] = useState(null);

  const API_BASE_URL = 'https://growthsprouts-server.vercel.app/api';

  useEffect(() => {
    const loadPendingDeposits = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/deposits/admin/pending`);
        const result = await response.json();
        
        if (result.success) {
          console.log('Pending deposits loaded:', result.data);
          // Log the first deposit to see structure
          if (result.data && result.data.length > 0) {
            console.log('First deposit sample:', result.data[0]);
            console.log('First deposit image URL:', result.data[0].proof_image_url);
          }
          setPendingDeposits(result.data || []);
        }
      } catch (error) {
        console.error('Error loading deposits:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPendingDeposits();
    const interval = setInterval(loadPendingDeposits, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleImageClick = (imageUrl) => {
    console.log('Image URL clicked:', imageUrl);
    console.log('URL type:', typeof imageUrl);
    console.log('URL valid:', !!imageUrl);
    
    if (!imageUrl) {
      alert('No image URL available');
      return;
    }
    
    // Try opening the URL
    try {
      const newWindow = window.open(imageUrl, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.error('Popup blocked or failed to open');
        alert('Could not open image. Please check if popups are blocked.');
      }
    } catch (error) {
      console.error('Error opening image:', error);
      alert('Error opening image: ' + error.message);
    }
  };

  const handleDepositDecision = async (depositId, approved, rejectionReason = null) => {
    try {
      setProcessingDeposit(depositId);
      const response = await fetch(`${API_BASE_URL}/deposits/${depositId}/admin-approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approved: approved,
          admin_id: currentUser.id,
          rejection_reason: rejectionReason
        }),
      });

      const result = await response.json();
      
      if (result.success) {
        setPendingDeposits(prev => prev.filter(d => d.id !== depositId));
        alert(approved 
          ? `Deposit approved! $${result.data.amount} added to account.`
          : 'Deposit rejected and parents notified.'
        );
      } else {
        alert('Failed to process deposit. Please try again.');
      }
    } catch (error) {
      alert('Error processing deposit. Please try again.');
    } finally {
      setProcessingDeposit(null);
    }
  };

  const handleReject = (depositId) => {
    const reason = prompt('Reason for rejection:');
    if (reason && reason.trim()) {
      handleDepositDecision(depositId, false, reason.trim());
    }
  };

  const getAvatarEmoji = (avatar) => {
    const avatars = { sprout: '🌱', leaf: '🌿', tree: '🌳' };
    return avatars[avatar] || '🌱';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Pending Approvals</h2>
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <Card className="p-8 text-center">
          <p className="text-gray-500">Loading...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Pending Approvals</h2>
          <p className="text-gray-600 text-sm">Review deposit proof submissions</p>
        </div>
        <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-3 py-1 rounded-full font-medium text-sm">
          {pendingDeposits.length} pending
        </div>
      </div>

      <div className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto">
        {pendingDeposits.length === 0 ? (
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up!</h3>
            <p className="text-gray-600">No deposits pending approval.</p>
          </Card>
        ) : (
          pendingDeposits.map((deposit) => (
            <Card key={deposit.id} className="overflow-hidden shadow-lg border border-orange-200/50">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center shadow-lg text-lg">
                      {getAvatarEmoji(deposit.child_avatar)}
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900">
                        {deposit.child_first_name} {deposit.child_last_name}
                      </h3>
                      <p className="text-sm text-gray-600">{deposit.family_name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-green-600 bg-clip-text text-transparent">
                      ${deposit.amount.toFixed(2)}
                    </div>
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                      <Clock className="w-3 h-3 mr-1" />
                      Awaiting Review
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Description:</span>
                      <p className="font-medium text-gray-900">{deposit.description}</p>
                    </div>
                    <div>
                      <span className="text-gray-500">Requested:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(deposit.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Parent:</span>
                      <p className="font-medium text-gray-900">
                        {deposit.parent_first_name} {deposit.parent_last_name}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Proof Submitted:</span>
                      <p className="font-medium text-gray-900">
                        {new Date(deposit.verified_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {deposit.proof_image_url && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Deposit Proof Image:
                    </label>
                    
                    {/* Debug info - remove in production */}
                    <div className="mb-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <strong>Debug URL:</strong> {deposit.proof_image_url}
                    </div>
                    
                    <div className="relative group">
                      <img
                        src={deposit.proof_image_url}
                        alt="Deposit proof"
                        className="w-full h-32 object-cover rounded-xl border border-gray-200 cursor-pointer hover:opacity-90 transition-all duration-200 group-hover:shadow-lg"
                        onClick={() => handleImageClick(deposit.proof_image_url)}
                        onError={(e) => {
                          console.error('Image failed to load:', deposit.proof_image_url);
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'block';
                        }}
                      />
                      <div className="hidden bg-red-50 border border-red-200 rounded-xl p-4 text-center">
                        <p className="text-red-600 text-sm">❌ Image failed to load</p>
                        <p className="text-xs text-red-500 mt-1">URL: {deposit.proof_image_url}</p>
                      </div>
                      <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <div className="bg-white/90 backdrop-blur-sm rounded-full p-3 shadow-lg">
                          <ExternalLink className="w-5 h-5 text-gray-700" />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2 flex items-center">
                      <Camera className="w-3 h-3 mr-1" />
                      Click to open in new tab
                    </p>
                  </div>
                )}

                <div className="flex items-center space-x-3">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handleReject(deposit.id)}
                    disabled={processingDeposit === deposit.id}
                    className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => handleDepositDecision(deposit.id, true)}
                    disabled={processingDeposit === deposit.id}
                    className="flex-1 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  >
                    {processingDeposit === deposit.id ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Processing...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve & Add to Balance
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default AdminDepositsPanel;